import {
  describeApiError,
  type PatchTournamentRequest,
  type TournamentStaffDetail as TournamentRecord,
  type TournamentListItem,
  type TournamentSort,
} from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Spinner } from '@brush/ui';
import { Check, ChevronRight, FileUp, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  useCreateTournament,
  useDeleteTournament,
  useApplyTournamentTags,
  useCreatePlayer,
  usePatchTournament,
  usePlatformTags,
  useRoomSummary,
  useRoomToday,
  useRoomTournamentPage,
  useTournament,
  useTournamentTagUsage,
} from '../../queries';
import { useRoom } from '../../room';
import { useMainScroll } from '../../scroll';
import { rankedTagsFromUsage, tagsForDisplay } from '../../tags';
import { bulkTagSelection, openedTournament, standingsState, toRoomListParams } from '../../tournamentList';
import {
  UNFILTERED,
  clearSavedFilter,
  initialFilter,
  loadSavedFilter,
  sameFilter,
  saveFilter,
  type TournamentFilter,
} from '../../tournamentFilter';
import { playersToCreate, toResults, type ResultDraft } from '../../results';
import { DEFAULT_SORT, nextSort, type TournamentSortState } from '../../tournamentSort';
import { OverflowMenu, overflowMenuItem } from '../OverflowMenu';
import { ExportControl } from '../ExportControl';
import { pageOf } from '../../export/exportControl';
import { StatusChip, TournamentTags } from './cells';
import { TournamentCard } from './TournamentCard';
import { TournamentSortSelect } from './TournamentSortSelect';
import { CreateTournamentModal } from './CreateTournamentModal';
import { EditTagsModal } from './EditTagsModal';
import { ImportResultsModal } from './import/ImportResultsModal';
import { ImportTournamentsModal } from './import/ImportTournamentsModal';
import { EditTournamentModal } from './EditTournamentModal';
import { Pager } from './Pager';
import { SortHeader } from './SortHeader';
import { TournamentDetail } from './TournamentDetail';
import { BulkTagModal } from './BulkTagModal';
import { TournamentFilterBar } from './TournamentFilterBar';
import { UploadResultsModal } from './UploadResultsModal';


export function TournamentsView() {
  const { room, roomId } = useRoom();

  // The filter opens on whatever this room's operator saved, or this month.
  const [filter, setFilter] = useState<TournamentFilter>(() => initialFilter(roomId));
  const [savedDefault, setSavedDefault] = useState(() =>
    roomId === null ? null : loadSavedFilter(roomId),
  );
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<TournamentSortState>(DEFAULT_SORT);
  const [filterRoom, setFilterRoom] = useState(roomId);
  const [bulkTagging, setBulkTagging] = useState(false);

  // Switching rooms adopts that room's own saved filter. Adjusting state during render is
  // React's documented alternative to an effect here, and avoids a throwaway fetch with the
  // previous room's filter.
  if (filterRoom !== roomId) {
    setFilterRoom(roomId);
    setFilter(initialFilter(roomId));
    setSavedDefault(roomId === null ? null : loadSavedFilter(roomId));
    setPage(1);
  }

  // Sort rides alongside the filter rather than inside it — it changes the order of the
  // matched set, not which rows match — but both are part of the query key either way.
  const params = useMemo(
    () => ({ ...toRoomListParams(filter, page), sort: sort.column, dir: sort.direction }),
    [filter, page, sort],
  );
  // The room's list as a list shape: the rows the table draws and nothing of their children
  // (performance rule 2). A date preset is resolved by the server at the room's day.
  const pageQ = useRoomTournamentPage(roomId, params);

  /*
   * The tag vocabulary, with how many tournaments carry each tag, from the server (performance rule
   * 3): the whole room's tags, not the page's, or the chips would come and go as you page. It used to be
   * counted over the whole tournament list. Whether the room holds any tournament at all comes from the
   * summary the shell already has. The roster is asked for only once a tournament is open, which is
   * the only place this screen names a player (gate answer Q8).
   */
  const tagUsageQ = useTournamentTagUsage(roomId);
  const summaryQ = useRoomSummary(roomId);
  const todayQ = useRoomToday(roomId);
  const platformTagsQ = usePlatformTags();

  const createTournament = useCreateTournament(roomId);
  const patchTournament = usePatchTournament(roomId);
  const deleteTournament = useDeleteTournament(roomId);
  const applyTags = useApplyTournamentTags(roomId);
  const createPlayer = useCreatePlayer(roomId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingTagsFor, setEditingTagsFor] = useState<TournamentListItem | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);
  // Which tournament a results editor is open for. The editors need its results, which the page no
  // longer carries, so each opens once the tournament's own record has arrived.
  const [editorFor, setEditorFor] = useState<{ id: string; editor: 'upload' | 'import' } | null>(null);
  const [resultsError, setResultsError] = useState<string | null>(null);

  // The opened tournament's own record — results and sittings, each result naming its finisher — asked
  // for only while one is open. It names the finishers itself, so the room's roster is not read here.
  const fullId = selectedId ?? editorFor?.id ?? null;
  const fullQ = useTournament(roomId, fullId);

  const editorTarget: TournamentRecord | null =
    editorFor !== null && fullQ.data?.id === editorFor.id ? fullQ.data : null;
  const upload = editorFor?.editor === 'upload' ? editorTarget : null;
  const importResultsFor = editorFor?.editor === 'import' ? editorTarget : null;
  const editorWaiting = editorFor !== null && editorTarget === null && !fullQ.isError;
  const openEditor = (id: string, editor: 'upload' | 'import') => setEditorFor({ id, editor });
  const closeEditor = () => setEditorFor(null);

  const tagUsage = useMemo(() => tagUsageQ.data ?? [], [tagUsageQ.data]);
  const allTags = useMemo(
    () => tagUsage.map((u) => u.tag).sort((a, b) => a.localeCompare(b)),
    [tagUsage],
  );

  /*
   * Each of these replaces what the main area is showing, and the replacement is routinely
   * shorter than what it replaced — a tournament opened from the foot of a long list, a page
   * size dropped from 100 to 25, a filter that now matches four rows. The scroll position
   * survives the swap, so without this the operator is left looking at the blank space below
   * the new screen, which reads as an empty section rather than as being scrolled past it.
   */
  const scrollToTop = useMainScroll();
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, selectedId, page, sort, filter]);

  if (pageQ.isLoading && pageQ.data === undefined) return <Spinner label="Loading tournaments…" />;
  if (pageQ.isError || !room) {
    return (
      <div className="p-8">
        <ErrorRetry message="Couldn't load tournaments." onRetry={() => void pageQ.refetch()} />
      </div>
    );
  }

  const rows = pageQ.data?.data ?? [];
  const total = pageQ.data?.total ?? 0;
  const platformTags = platformTagsQ.data ?? [];
  const saving = patchTournament.isPending;

  // What the tag inputs offer: everything the room already uses plus the platform's own, ordered
  // by how often this room actually applies each one. The filter bar keeps its own alphabetical
  // list — there you are looking for a particular tag, here you are reaching for a usual one.
  const tagVocabulary = rankedTagsFromUsage(tagUsage, platformTags);

  const saveTags = (id: string, tags: string[]) => patchTournament.mutate({ id, body: { tags } });
  /**
   * A sheet can name players the room has never carded, so they are created first and their new
   * ids folded into the rows that made them.
   *
   * Not atomic, and deliberately in this order: a player created without their result is a
   * harmless duplicate-free record the operator can use again, while a result written against a
   * player id that does not exist is a foreign key violation. If a create fails the whole save
   * stops before any result is written, and the error notice says which row.
   */
  const saveResults = async (id: string, drafts: ResultDraft[]) => {
    if (roomId === null) return;
    setResultsError(null);

    const created = new Map<string, string>();
    try {
      for (const { key, ...body } of playersToCreate(drafts, roomId)) {
        const player = await createPlayer.mutateAsync(body);
        created.set(key, player.id);
      }
    } catch (error) {
      setResultsError(
        `${describeApiError(error) ?? 'Could not create a new player.'} No results were saved.`,
      );
      return;
    }

    patchTournament.mutate(
      { id, body: { results: toResults(drafts, created) } },
      { onSuccess: closeEditor },
    );
  };
  const saveDetails = (id: string, patch: Partial<PatchTournamentRequest>) =>
    patchTournament.mutate({ id, body: patch }, { onSuccess: () => setEditingDetails(false) });

  // The record is gone, so the detail view it was showing has to close with it.
  const removeTournament = (id: string) =>
    deleteTournament.mutate(id, {
      onSuccess: () => {
        setEditingDetails(false);
        setSelectedId(null);
      },
    });

  /*
   * Detail view. The page's rows no longer carry results or sittings, so an opened tournament shows its
   * row at once and its own record when that arrives (gate answer Q11) — the results loading beneath,
   * never a blank panel. The record is the one shown once it is there, and keeps a tournament an edit
   * has just moved off the page. Editing and entering results wait for it: each needs the results or
   * the sittings.
   */
  const opened = openedTournament(rows, selectedId, fullQ.data);
  if (opened) {
    const { row, full } = opened;
    return (
      <>
        {/* The detail view is an early return, so it needs its own notice — editing or
            uploading results here would otherwise fail silently. */}
        <ErrorNotice
          message={describeApiError(patchTournament.error) ?? describeApiError(deleteTournament.error)}
          className="mx-8 mt-8 -mb-4"
        />
        <TournamentDetail
          tournament={row}
          full={full}
          sheet={standingsState({ full, resultsFailed: fullQ.isError })}
          platformTags={platformTags}
          onEdit={() => setEditingDetails(true)}
          onUploadResults={() => openEditor(row.id, 'upload')}
          onImportResults={() => openEditor(row.id, 'import')}
          onBack={() => {
            setEditingDetails(false);
            setSelectedId(null);
          }}
        />
        {editingDetails && full ? (
          <EditTournamentModal
            tournament={full}
            allTags={tagVocabulary}
            platformTags={platformTags}
            saving={saving}
            deleting={deleteTournament.isPending}
            onSave={(details) => saveDetails(full.id, details)}
            onDelete={() => removeTournament(full.id)}
            onClose={() => setEditingDetails(false)}
          />
        ) : null}
        {editorWaiting ? <ResultsLoading /> : null}
        {upload ? (
          <UploadResultsModal
            roomId={room.id}
            tournament={upload}
            saving={saving || createPlayer.isPending}
            errorMessage={resultsError}
            onSave={(drafts) => void saveResults(upload.id, drafts)}
            onClose={closeEditor}
          />
        ) : null}

        {importResultsFor && roomId !== null ? (
          <ImportResultsModal
            roomId={roomId}
            tournament={importResultsFor}
            onClose={closeEditor}
          />
        ) : null}
      </>
    );
  }

  const changeFilter = (next: TournamentFilter) => {
    setFilter(next);
    setPage(1); // a narrower filter can leave the current page beyond the end of the results
  };

  const changeSort = (column: TournamentSort) => {
    setSort((current) => nextSort(current, column));
    setPage(1); // row 30 of one order is a different tournament under another
  };

  // The phone's sort control names a column *and* a direction outright, where the table's headings
  // toggle. Same reset for the same reason.
  const setSortState = (next: TournamentSortState) => {
    setSort(next);
    setPage(1);
  };


  const roomHasTournaments = (summaryQ.data?.counts.tournaments ?? total) > 0;

  /*
   * Written once and rendered by both arrangements. Two copies would be two answers to "why is
   * this list empty", and the distinction it draws — a room with no tournaments at all, versus a
   * filter that matched none — is the one an operator needs to tell apart.
   */
  const emptyState = !roomHasTournaments ? (
    'No tournaments yet. Create your first one to get started.'
  ) : (
    <>
      No tournaments match this filter.
      <button
        type="button"
        onClick={() => changeFilter({ ...UNFILTERED, pageSize: filter.pageSize })}
        className="ml-1.5 font-medium text-felt-700 underline underline-offset-2 hover:text-felt-900"
      >
        Show all
      </button>
    </>
  );

  return (
    <div className="p-8">
      {/*
       * Two arrangements, not one that degrades. From `md` both actions are buttons. Below it the
       * heading and the primary button alone come to about 355px of the 366px `p-8` leaves at the
       * 430px reference width, so the second button cannot stay — and `flex-wrap` is the valve if
       * a longer room name or a wider face pushes past that margin.
       *
       * Import is the one demoted rather than New because of what each costs a phone: importing
       * needs a CSV to hand and a four-step wizard whose review step is a per-tournament diff
       * table, which is desk work. Creating is what somebody does standing up when a table fills
       * unexpectedly, and every other section view puts its single `Plus` action one tap away —
       * so making this one two taps would be consistent in appearance and not in behaviour.
       */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold text-ink-950">Tournaments</h1>
          <p className="mt-1 text-sm text-ink-800/60">Manage tournaments and record results.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* T1 (docs/specs/export.md EX4): the screen's own filter and sort over every page, never the paging. Print
              shows this page, and says which. */}
          <ExportControl
            availability={{ view: 'T1' }}
            target={roomId ? { view: 'T1', roomId, filter: params } : null}
            viewLabel="Tournaments"
            subject="Tournaments"
            page={pageOf(page, filter.pageSize, total)}
          />
          {/* Results are recorded against a tournament you already have open — from its row or
              its detail view — so there is no results button up here to pick one first. */}
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="hidden items-center gap-2 rounded-lg border border-ink-800/15 px-4 py-2 text-sm text-ink-800 hover:bg-white md:inline-flex"
          >
            <FileUp className="size-4" /> Import tournaments
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white hover:bg-felt-900"
          >
            <Plus className="size-4" /> New tournament
          </button>
          {/* The same handler as the button above, so the two arrangements cannot come adrift. */}
          <OverflowMenu label="More tournament actions">
            {(close) => (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setShowImport(true);
                }}
                className={overflowMenuItem}
              >
                <FileUp className="size-4" aria-hidden /> Import tournaments
              </button>
            )}
          </OverflowMenu>
        </div>
      </div>

      <ErrorNotice
        message={describeApiError(createTournament.error) ?? describeApiError(patchTournament.error)}
        className="mb-4"
      />

      <TournamentFilterBar
        value={filter}
        onChange={changeFilter}
        allTags={allTags}
        total={total}
        loading={pageQ.isFetching}
        isDefault={savedDefault !== null && sameFilter(filter, savedDefault)}
        onSaveDefault={() => {
          if (roomId === null) return;
          saveFilter(roomId, filter);
          setSavedDefault(filter);
        }}
        onClearDefault={() => {
          if (roomId === null) return;
          clearSavedFilter(roomId);
          setSavedDefault(null);
        }}
        onBulkTag={() => setBulkTagging(true)}
      />

      <div className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
        {/*
         * Two arrangements, not one that degrades — the same rule the header follows. The table
         * cannot fit 430px at any padding worth reading, and the card is not a narrower table: it
         * carries fewer facts, drops two of the three row actions to the detail view, and moves
         * sorting into a control of its own, since the column headings that sort the table have
         * nowhere to go. `Pager` below is shared, being the one part that already wraps.
         */}
        <div className="md:hidden print:hidden">
          {rows.length > 0 ? (
            <>
              <TournamentSortSelect sort={sort} onSort={setSortState} />
              <div className="divide-y divide-ink-800/5">
                {rows.map((t) => (
                  <TournamentCard
                    key={t.id}
                    tournament={t}
                    shownTags={tagsForDisplay(t.tags, filter.tags)}
                    platformTags={platformTags}
                    onOpen={() => setSelectedId(t.id)}
                    onEnterResults={() => openEditor(t.id, 'upload')}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="px-6 py-10 text-center text-sm text-ink-800/60">{emptyState}</div>
          )}
        </div>

        <div className="hidden overflow-x-auto md:block print:block print:overflow-visible">
          <table className="w-full text-left text-sm">
            <thead className="bg-felt-50/50 text-xs uppercase tracking-wide text-ink-800/60">
              <tr>
                <SortHeader column="name" label="Tournament" sort={sort} onSort={changeSort} />
                <SortHeader column="date" label="Date" sort={sort} onSort={changeSort} />
                <SortHeader column="buyIn" label="Buy-in" sort={sort} onSort={changeSort} />
                <SortHeader column="entries" label="Entries" sort={sort} onSort={changeSort} />
                {/* Tags and results are collections, and status sorts by its stored wire string
                    rather than anything an operator would recognise as an order. */}
                <th className="px-6 py-3 font-medium">Tags</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Results</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-800/5">
              {rows.map((t) => (
                <tr key={t.id} className="hover:bg-felt-50/30">
                  <td
                    className="cursor-pointer px-6 py-4 font-medium text-ink-950"
                    onClick={() => setSelectedId(t.id)}
                  >
                    {t.name}
                  </td>
                  <td
                    className="cursor-pointer px-6 py-4 text-ink-800/70"
                    onClick={() => setSelectedId(t.id)}
                  >
                    {t.date}
                  </td>
                  <td
                    className="cursor-pointer px-6 py-4 text-ink-950"
                    onClick={() => setSelectedId(t.id)}
                  >
                    ${t.buyIn.toLocaleString()}
                  </td>
                  <td
                    className="cursor-pointer px-6 py-4 text-ink-950"
                    onClick={() => setSelectedId(t.id)}
                  >
                    {t.entries}
                  </td>
                  <td className="px-6 py-4">
                    {/* The tags being filtered on lead the cell, so the three that fit never
                        truncate away the one the operator filtered by. The count is of every tag,
                        which reordering does not change. */}
                    <TournamentTags
                      tags={t.tags}
                      shown={tagsForDisplay(t.tags, filter.tags)}
                      platformTags={platformTags}
                      onEdit={() => setEditingTagsFor(t)}
                    />
                  </td>
                  <td className="cursor-pointer px-6 py-4" onClick={() => setSelectedId(t.id)}>
                    <StatusChip status={t.status} />
                  </td>
                  {/* A file and a keyboard are two ways at the same job, so both are offered from
                      the row — the same pairing the detail view puts in its header, rather than
                      one of them reachable only after opening the tournament. */}
                  <td className="px-6 py-4">
                    {/* `entries` rather than `results.length`, and they are the same number by
                        construction: the field is on no create or patch request, and the server
                        sets it to the result row count on every results write. Reading the count
                        off the count is what will let the list stop shipping the results array
                        at all. */}
                    {t.entries > 0 ? (
                      <span className="mb-1 flex items-center gap-1 text-xs text-felt-700">
                        <Check className="size-3" /> {t.entries} entries
                      </span>
                    ) : null}
                    <div className="flex items-center gap-2 whitespace-nowrap text-xs print:hidden">
                      <button
                        type="button"
                        onClick={() => openEditor(t.id, 'upload')}
                        className="font-medium text-felt-700 underline underline-offset-2 hover:text-felt-900"
                      >
                        {t.entries > 0 ? 'Update' : 'Enter'}
                      </button>
                      <span className="text-ink-800/25" aria-hidden>
                        ·
                      </span>
                      <button
                        type="button"
                        onClick={() => openEditor(t.id, 'import')}
                        className="inline-flex items-center gap-1 font-medium text-felt-700 underline underline-offset-2 hover:text-felt-900"
                      >
                        <FileUp className="size-3" /> Import CSV
                      </button>
                    </div>
                  </td>
                  <td className="cursor-pointer px-6 py-4" onClick={() => setSelectedId(t.id)}>
                    <ChevronRight className="size-4 text-ink-800/30" />
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center text-sm text-ink-800/60">
                    {emptyState}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Pager
          page={page}
          pageSize={filter.pageSize}
          total={total}
          onPage={setPage}
          // Goes through changeFilter, so a bigger page can't leave you stranded past the end.
          onPageSize={(pageSize) => changeFilter({ ...filter, pageSize })}
        />
      </div>

      {/* Opens dated by the room's own day, so it waits for the today read, which starts with the page. */}
      {showCreate && todayQ.data ? (
        <CreateTournamentModal
          roomId={room.id}
          today={todayQ.data.day}
          allTags={tagVocabulary}
          platformTags={platformTags}
          defaultTags={room.defaultTags}
          saving={createTournament.isPending}
          onSave={(body) => createTournament.mutate(body, { onSuccess: () => setShowCreate(false) })}
          onClose={() => setShowCreate(false)}
        />
      ) : null}

      {showImport ? (
        <ImportTournamentsModal
          roomId={room.id}
          defaultTags={room.defaultTags}
          onClose={() => setShowImport(false)}
        />
      ) : null}

      {editorWaiting ? <ResultsLoading /> : null}
      {upload ? (
        <UploadResultsModal
          roomId={room.id}
          tournament={upload}
          saving={saving || createPlayer.isPending}
          errorMessage={resultsError}
          onSave={(drafts) => void saveResults(upload.id, drafts)}
          onClose={closeEditor}
        />
      ) : null}

      {importResultsFor && roomId !== null ? (
        <ImportResultsModal
          roomId={roomId}
          tournament={importResultsFor}
          onClose={closeEditor}
        />
      ) : null}

      {editingTagsFor ? (
        <EditTagsModal
          tournament={editingTagsFor}
          allTags={tagVocabulary}
          platformTags={platformTags}
          saving={saving}
          onSave={(tags) => {
            saveTags(editingTagsFor.id, tags);
            setEditingTagsFor(null);
          }}
          onClose={() => setEditingTagsFor(null)}
        />
      ) : null}

      {bulkTagging && roomId !== null ? (
        <BulkTagModal
          filter={filter}
          matched={total}
          allTags={tagVocabulary}
          platformTags={platformTags}
          saving={applyTags.isPending}
          errorMessage={describeApiError(applyTags.error)}
          onApply={(add, remove) => {
            // The same selection the list is showing — the dates the page says it applied, not a
            // preset worked out here — so the rows written to are the rows counted in the button.
            // No paging: this is the whole matched set. Before a page has answered there is no
            // selection to write to, and a request without one would tag every tournament.
            const selection = bulkTagSelection(filter, pageQ.data);
            if (selection === null) return;
            applyTags.mutate(
              { ...selection, roomId, add, remove },
              { onSuccess: () => setBulkTagging(false) },
            );
          }}
          onClose={() => setBulkTagging(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Shown while a results editor waits for the tournament's own record — the page no longer carries its
 * results. Brief on a fast link; on staging's, the largest event's results are a few hundred kilobytes.
 */
function ResultsLoading() {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/20" role="status">
      <div className="bg-white px-6 py-4 shadow-sm">
        <Spinner label="Loading results…" />
      </div>
    </div>
  );
}
